export type MenuCategory = "main" | "side" | "drink" | "dessert";
export type Role = "customer" | "waiterChef";
export type Seat = "host" | "guest";
export type GamePhase =
  | "lobby"
  | "customer-order"
  | "waiter-order"
  | "kitchen"
  | "review"
  | "bill"
  | "paid";

export interface Extra {
  id: string;
  name: string;
  emoji: string;
  pricePence: number;
}

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  emoji: string;
  category: MenuCategory;
  pricePence: number;
  allowedExtraIds: string[];
}

export interface RestaurantConfig {
  name: string;
  menuItemIds: string[];
}

export interface ParticipantView {
  seat: Seat;
  role: Role | null;
  connected: boolean;
}

export interface OrderLine {
  itemId: string;
  extraIds: string[];
}

export interface OrderSelection {
  lines: OrderLine[];
}

export interface ChefTray {
  componentIds: string[];
}

export interface ComparisonResult {
  missing: string[];
  extra: string[];
  exact: boolean;
}

export interface RoundComparison {
  waiter: ComparisonResult;
  kitchen: ComparisonResult;
}

export interface Bill {
  totalPence: number;
  lines: Array<{ label: string; pricePence: number }>;
  paid: boolean;
}

export interface RoomView {
  code: string;
  config: RestaurantConfig;
  version: number;
  phase: GamePhase;
  round: number;
  you: { seat: Seat; role: Role | null };
  participants: ParticipantView[];
  customerOrder?: OrderSelection;
  waiterTicket?: OrderSelection;
  chefTray?: ChefTray;
  comparison?: RoundComparison;
  bill?: Bill;
  expiresAt: number;
}

export type GameAction =
  | { type: "set_role"; payload: { role: Role } }
  | { type: "lock_customer_order"; payload: { order: OrderSelection } }
  | { type: "submit_waiter_order"; payload: { order: OrderSelection } }
  | { type: "serve_tray"; payload: { tray: ChefTray } }
  | { type: "fix_waiter"; payload: Record<string, never> }
  | { type: "fix_kitchen"; payload: Record<string, never> }
  | { type: "accept_order"; payload: Record<string, never> }
  | { type: "pay_bill"; payload: Record<string, never> }
  | { type: "new_round"; payload: Record<string, never> };

export interface ActionEnvelope {
  actionId: string;
  expectedVersion: number;
  action: GameAction;
}

export type ServerMessage =
  | { type: "state"; view: RoomView }
  | { type: "error"; message: string; view?: RoomView };

export const EXTRAS: Extra[] = [
  { id: "extra-cheese", name: "Extra cheese", emoji: "🧀", pricePence: 50 },
  { id: "bacon", name: "Crispy bacon", emoji: "🥓", pricePence: 100 },
  { id: "extra-patty", name: "Extra burger", emoji: "🥩", pricePence: 200 },
  { id: "pepperoni", name: "Pepperoni", emoji: "🍕", pricePence: 100 },
  { id: "mushrooms", name: "Mushrooms", emoji: "🍄", pricePence: 50 },
  { id: "meatballs", name: "Meatballs", emoji: "🧆", pricePence: 150 },
  { id: "parmesan", name: "Parmesan", emoji: "🧀", pricePence: 50 },
  { id: "beans", name: "Baked beans", emoji: "🥣", pricePence: 50 },
  { id: "tuna", name: "Tuna", emoji: "🐟", pricePence: 100 },
  { id: "ketchup", name: "Ketchup", emoji: "🔴", pricePence: 0 },
  { id: "mayo", name: "Mayonnaise", emoji: "⚪", pricePence: 0 },
  { id: "sprinkles", name: "Sprinkles", emoji: "✨", pricePence: 50 },
  { id: "chocolate-sauce", name: "Chocolate sauce", emoji: "🍫", pricePence: 50 },
  { id: "ice-cream-scoop", name: "Ice cream scoop", emoji: "🍨", pricePence: 100 }
];

export const MENU_ITEMS: MenuItem[] = [
  { id: "classic-burger", name: "Classic Burger", description: "Beef burger in a soft bun", emoji: "🍔", category: "main", pricePence: 650, allowedExtraIds: ["extra-cheese", "bacon", "extra-patty", "ketchup", "mayo"] },
  { id: "margherita-pizza", name: "Margherita Pizza", description: "Tomato, mozzarella and herbs", emoji: "🍕", category: "main", pricePence: 700, allowedExtraIds: ["pepperoni", "mushrooms", "extra-cheese"] },
  { id: "tomato-pasta", name: "Tomato Pasta", description: "Pasta twists in tomato sauce", emoji: "🍝", category: "main", pricePence: 650, allowedExtraIds: ["meatballs", "parmesan", "extra-cheese"] },
  { id: "jacket-potato", name: "Jacket Potato", description: "A fluffy oven-baked potato", emoji: "🥔", category: "main", pricePence: 500, allowedExtraIds: ["extra-cheese", "beans", "tuna"] },
  { id: "fries", name: "Crispy Fries", description: "Golden and crunchy", emoji: "🍟", category: "side", pricePence: 250, allowedExtraIds: ["ketchup", "mayo"] },
  { id: "garden-salad", name: "Garden Salad", description: "Lettuce, cucumber and tomato", emoji: "🥗", category: "side", pricePence: 250, allowedExtraIds: [] },
  { id: "garlic-bread", name: "Garlic Bread", description: "Warm, buttery slices", emoji: "🥖", category: "side", pricePence: 300, allowedExtraIds: ["extra-cheese"] },
  { id: "water", name: "Water", description: "Chilled still water", emoji: "💧", category: "drink", pricePence: 100, allowedExtraIds: [] },
  { id: "orange-juice", name: "Orange Juice", description: "A bright, fruity glass", emoji: "🍊", category: "drink", pricePence: 200, allowedExtraIds: [] },
  { id: "lemonade", name: "Lemonade", description: "Cold and fizzy", emoji: "🍋", category: "drink", pricePence: 200, allowedExtraIds: [] },
  { id: "milkshake", name: "Milkshake", description: "Thick vanilla shake", emoji: "🥤", category: "drink", pricePence: 300, allowedExtraIds: ["sprinkles", "chocolate-sauce"] },
  { id: "ice-cream", name: "Ice Cream", description: "Two scoops in a bowl", emoji: "🍨", category: "dessert", pricePence: 350, allowedExtraIds: ["sprinkles", "chocolate-sauce"] },
  { id: "brownie", name: "Chocolate Brownie", description: "Warm and gooey", emoji: "🍫", category: "dessert", pricePence: 400, allowedExtraIds: ["ice-cream-scoop", "chocolate-sauce"] },
  { id: "fruit-pot", name: "Fruit Pot", description: "A colourful fruit mix", emoji: "🍓", category: "dessert", pricePence: 250, allowedExtraIds: [] }
];

const itemById = new Map(MENU_ITEMS.map((item) => [item.id, item]));
const extraById = new Map(EXTRAS.map((extra) => [extra.id, extra]));

export function getMenuItem(id: string): MenuItem | undefined {
  return itemById.get(id);
}

export function getExtra(id: string): Extra | undefined {
  return extraById.get(id);
}

export function formatMoney(pence: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

export function validateRestaurantConfig(value: unknown): value is RestaurantConfig {
  if (!value || typeof value !== "object") return false;
  const config = value as Partial<RestaurantConfig>;
  if (typeof config.name !== "string" || config.name.trim().length < 1 || config.name.trim().length > 30) return false;
  if (!Array.isArray(config.menuItemIds) || config.menuItemIds.length < 4 || config.menuItemIds.length > MENU_ITEMS.length) return false;
  if (new Set(config.menuItemIds).size !== config.menuItemIds.length) return false;
  const items = config.menuItemIds.map((id) => typeof id === "string" ? itemById.get(id) : undefined);
  return items.every(Boolean) && items.some((item) => item?.category === "main");
}

export function validateOrder(value: unknown, allowedMenuItemIds: string[]): value is OrderSelection {
  if (!value || typeof value !== "object") return false;
  const order = value as Partial<OrderSelection>;
  if (!Array.isArray(order.lines) || order.lines.length < 1 || order.lines.length > 4) return false;
  const categories = new Set<MenuCategory>();
  for (const line of order.lines) {
    if (!line || typeof line.itemId !== "string" || !Array.isArray(line.extraIds)) return false;
    const item = itemById.get(line.itemId);
    if (!item || !allowedMenuItemIds.includes(item.id) || categories.has(item.category)) return false;
    categories.add(item.category);
    if (line.extraIds.length > 5 || new Set(line.extraIds).size !== line.extraIds.length) return false;
    if (!line.extraIds.every((id) => typeof id === "string" && item.allowedExtraIds.includes(id) && extraById.has(id))) return false;
  }
  return categories.has("main");
}

export function orderComponents(order: OrderSelection): string[] {
  return order.lines.flatMap((line) => [line.itemId, ...line.extraIds]);
}

export function compareComponents(expected: string[], actual: string[]): ComparisonResult {
  const expectedCounts = countValues(expected);
  const actualCounts = countValues(actual);
  const missing: string[] = [];
  const extra: string[] = [];
  for (const [id, count] of expectedCounts) {
    const difference = count - (actualCounts.get(id) ?? 0);
    for (let index = 0; index < difference; index += 1) missing.push(id);
  }
  for (const [id, count] of actualCounts) {
    const difference = count - (expectedCounts.get(id) ?? 0);
    for (let index = 0; index < difference; index += 1) extra.push(id);
  }
  return { missing, extra, exact: missing.length === 0 && extra.length === 0 };
}

function countValues(values: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

export function compareRound(customer: OrderSelection, waiter: OrderSelection, tray: ChefTray): RoundComparison {
  const waiterComponents = orderComponents(waiter);
  return {
    waiter: compareComponents(orderComponents(customer), waiterComponents),
    kitchen: compareComponents(waiterComponents, tray.componentIds)
  };
}

export function buildBill(order: OrderSelection): Bill {
  const lines: Bill["lines"] = [];
  for (const line of order.lines) {
    const item = itemById.get(line.itemId);
    if (!item) continue;
    lines.push({ label: item.name, pricePence: item.pricePence });
    for (const extraId of line.extraIds) {
      const extra = extraById.get(extraId);
      if (extra) lines.push({ label: `+ ${extra.name}`, pricePence: extra.pricePence });
    }
  }
  return { totalPence: lines.reduce((sum, line) => sum + line.pricePence, 0), lines, paid: false };
}

export function componentLabel(id: string): { name: string; emoji: string } {
  const item = itemById.get(id);
  if (item) return { name: item.name, emoji: item.emoji };
  const extra = extraById.get(id);
  if (extra) return { name: extra.name, emoji: extra.emoji };
  return { name: "Unknown item", emoji: "❓" };
}
